// @ts-nocheck
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import socket from "../utilities/socket";
import { useCallStore } from "../router/zustand/callStore";
import {
  isMobileProfileActive,
  isSocketCallDrivenProfileActive,
  isWebrtcProfileActive,
  eventBelongsToAgent,
  normalizeCallEvent,
  rememberWebrtcCustomer,
} from "../utilities/mobileCallEvents";

const DEBUG_DEFAULT = false;

const CUSTOMER_RINGING_MAX_MS = 90000;
const SETUP_TONE_MAX_MS = 60000;

function useMobileCallSocket(agentId, agentNumber) {
  const lastEventKeyRef = useRef(null);
  const customerRingTimerRef = useRef(null);

  useEffect(() => {
    let ctx = null;
    let gain = null;
    let timer = null;
    let done = false;

    const stop = () => {
      clearInterval(timer);
      timer = null;
      try { ctx?.close(); } catch {}
      ctx = null;
      gain = null;
    };

    const start = () => {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      try {
        ctx = new AC();
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
        const osc = ctx.createOscillator();
        gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 350;
        gain.gain.value = 0;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        const startedAt = Date.now();
        const beat = () => {
          if (!gain) return;
          if (Date.now() - startedAt > SETUP_TONE_MAX_MS) {
            done = true;
            stop();
            return;
          }
          gain.gain.value = 0.04;
          setTimeout(() => { if (gain) gain.gain.value = 0; }, 200);
        };
        beat();
        timer = setInterval(beat, 1000);
      } catch {
        stop();
      }
    };

    const sync = (s) => {
      if (!s.isPlacingCall) {
        done = false;
        if (ctx) stop();
        return;
      }
      const setupOver =
        s.outgoingRingingActive || s.localHangupRequested || Boolean(s.activeCallMeta?.bridge_time);
      const agentRinging = s.outgoingCallStatus === "ringing-agent";
      if (ctx) {
        if (setupOver) { done = true; stop(); }
        else if (agentRinging) stop();
        return;
      }
      if (!done && !setupOver && !agentRinging && isMobileProfileActive()) start();
    };

    const unsub = useCallStore.subscribe(sync);
    return () => {
      unsub();
      stop();
    };
  }, []);

  useEffect(() => {
    if (!agentId) return undefined;

    const clearCustomerRingTimer = () => {
      if (customerRingTimerRef.current) {
        clearTimeout(customerRingTimerRef.current);
        customerRingTimerRef.current = null;
      }
    };

    const debugOn = () =>
      typeof window !== "undefined" && window.__CTI_MOBILE_DEBUG !== undefined
        ? Boolean(window.__CTI_MOBILE_DEBUG)
        : DEBUG_DEFAULT;

    const isDuplicate = (evt) => {
      const key = `${evt.eventName}:${evt.channelId || "?"}:${evt.timestamp || ""}`;
      if (key === lastEventKeyRef.current) return true;
      lastEventKeyRef.current = key;
      return false;
    };

    const startMeta = (evt) => ({
      direction: evt.direction,
      caller_number: evt.direction === "incoming" ? evt.number : agentNumber || null,
      callee_number: evt.direction === "incoming" ? agentNumber || null : evt.number,
      call_sid: evt.channelId || evt.traceId || "",
      trace_id: evt.traceId || null,
      account_code: evt.accountCode || null,
      agent_name: evt.agentName || null,
      customer_number: evt.customerNumber || null,
      start_time: evt.timestamp,
      caller_profile: [evt.leadData?.first_name, evt.leadData?.last_name].filter(Boolean).join(" ").trim() || null,
      company: evt.leadData?.company || null,
    });

    const lastTen = (value) => {
      const d = String(value ?? "").replace(/\D/g, "");
      if (!d) return null;
      return d.length > 10 ? d.slice(-10) : d;
    };

    const patchTraceId = (store, evt) => {
      if (evt?.traceId) store.updateActiveCallMeta?.({ trace_id: evt.traceId });
    };

    const ensureOutboundActiveCall = (store, evt, customer) => {
      if (store.activeCallMeta) return;
      const customerNumber = customer || store.callerNumber || null;
      store.startActiveCall({
        ...startMeta(evt),
        direction: "outgoing",
        caller_number: customerNumber,
        callee_number: customerNumber,
        customer_number: customerNumber,
        answer_status: "ringing",
      });
    };

    const isOutboundCall = (store) =>
      Boolean(store.isPlacingCall) ||
      store.direction === "outgoing" ||
      store.callState === "dialing" ||
      store.outgoingRingingActive;

    const handleDialing = (evt) => {
      const store = useCallStore.getState();
      if (evt.channelId) store.setSipData({ channelId: evt.channelId });

      if (!isOutboundCall(store)) return;

      const leg = evt.callLeg;
      if (leg === "A_PARTY") {
        const customer = lastTen(evt.number) || store.callerNumber || null;
        if (customer && store.callerNumber !== customer) {
          store.setOutgoingCall({ number: customer });
        } else if (store.callState !== "dialing") {
          store.setOutgoingCall({ number: customer });
        }
        store.setOutgoingCallStatus("calling-customer");
        ensureOutboundActiveCall(store, evt, customer);
        patchTraceId(store, evt);
        if (customer) {
          store.updateActiveCallMeta?.({
            caller_number: customer,
            callee_number: customer,
            customer_number: customer,
          });
        }
        return;
      }

      if (store.callState !== "dialing") {
        store.setOutgoingCall({ number: store.callerNumber || null });
      }
      store.setOutgoingCallStatus(leg === "B_PARTY" ? "calling-agent" : "dialing");
      ensureOutboundActiveCall(store, evt, store.callerNumber);
      patchTraceId(store, evt);
    };

    const handleRinging = (evt) => {
      const store = useCallStore.getState();
      const leg = evt.callLeg;

      if (isOutboundCall(store)) {
        if (evt.channelId) store.setSipData({ channelId: evt.channelId });

        if (leg === "A_PARTY") {
          const customer = lastTen(evt.number) || store.callerNumber || null;
          if (!store.outgoingRingingActive) {
            if (customer) store.setOutgoingCall({ number: customer });
            store.setOutgoingRinging(true);
          }
          store.setOutgoingCallStatus("ringing-customer");
          ensureOutboundActiveCall(store, evt, customer);
          patchTraceId(store, evt);
          // First ring of the customer leg — the live call timer starts here.
          if (!useCallStore.getState().activeCallMeta?.ring_time) {
            store.updateActiveCallMeta?.({ ring_time: evt.timestamp || new Date().toISOString() });
          }
          if (customer) {
            store.updateActiveCallMeta?.({
              caller_number: customer,
              callee_number: customer,
              customer_number: customer,
            });
          }
        } else {
          if (store.callState !== "dialing") {
            store.setOutgoingCall({ number: store.callerNumber || null });
          }
          store.setOutgoingCallStatus("ringing-agent");
          ensureOutboundActiveCall(store, evt, store.callerNumber);
          patchTraceId(store, evt);
        }
        return;
      }

      const alreadyHandlingCall =
        Boolean(store.activeCallMeta) ||
        store.isAnswering ||
        store.callState !== "idle";

      if (leg === "A_PARTY" && alreadyHandlingCall) {
        store.setSipData({
          channelId: evt.channelId || null,
          agentChannelId: evt.agentChannelId || null,
        });
        if (evt.customerNumber) {
          store.updateActiveCallMeta?.({ customer_number: evt.customerNumber });
        }
        store.setRingingActionsSuppressed?.(true);
        store.setCustomerRinging?.(true);
        clearCustomerRingTimer();
        customerRingTimerRef.current = setTimeout(() => {
          customerRingTimerRef.current = null;
          const s = useCallStore.getState();
          s.setCustomerRinging?.(false);
          s.setRingingActionsSuppressed?.(false);
        }, CUSTOMER_RINGING_MAX_MS);
        if (debugOn()) {
          console.log(
            "[MobileCallSocket] A_PARTY RINGING on in-progress call — customer ringing UI, no actions",
            { callState: store.callState, isAnswering: store.isAnswering }
          );
        }
        return;
      }

      const resolvedNumber = evt.number || store.callerNumber || null;
      const resolvedCustomer =
        evt.customerNumber || store.activeCallMeta?.customer_number || null;

      store.setIncomingCall({
        number: resolvedNumber,
        session: null,
        receivedAt: evt.timestamp,
        channelId: evt.channelId || null,
        agentChannelId: evt.agentChannelId || null,
        customerChannelId: evt.customerChannelId || null,
        callGroupId: evt.callGroupId || null,
      });
      store.setSipData({
        sipCampaignId: evt.campaignId || null,
        sipCallGroupId: evt.callGroupId || null,
        channelId: evt.channelId || null,
        agentChannelId: evt.agentChannelId || null,
        sipLeadData: evt.leadData || null,
      });
      // A mobile call has no SIP headers of its own — setSipData's `??`
      // merge treats a passed `null` the same as "not provided" (keeps
      // whatever's already there), so it can't actually clear this field;
      // set it directly instead. Without this, a stale `answer-mode: true`
      // left over from an earlier SIP call this session would silently
      // suppress useScreenState.ts's ringtone for this unrelated mobile call.
      useCallStore.setState({ sipHeaders: null });
      store.startActiveCall({
        ...startMeta(evt),
        caller_number: resolvedNumber,
        customer_number: resolvedCustomer,
        answer_status: "ringing",
      });
      patchTraceId(store, evt);
    };

    const handleAnswered = (evt) => {
      const store = useCallStore.getState();
      if (evt.channelId) store.setSipData({ channelId: evt.channelId });
      patchTraceId(store, evt);
      clearCustomerRingTimer();
      if (store.customerRingingActive) {
        store.setCustomerRinging?.(false);
        store.setRingingActionsSuppressed?.(false);
      }

      if (!store.isAnswering && isOutboundCall(store) && evt.callLeg === "B_PARTY") {
        if (store.callState !== "dialing") {
          store.setOutgoingCall({ number: store.callerNumber || null });
        }
        store.setOutgoingCallStatus("agent-connected");
        return;
      }

      const wasOutbound = isOutboundCall(store);
      store.setOutgoingRinging(false);
      store.setAnswering(false);
      if (wasOutbound) store.setOutgoingCallStatus("customer-connected");
      store.markActiveCallAnswered({
        answer_status: "answered",
        bridge_time: evt.timestamp,
      });
      if (evt.callLeg === "A_PARTY") store.updateActiveCallMeta?.({ customer_connected: true });
      store.setConnected();
    };

    const handleHangup = (evt) => {
      const store = useCallStore.getState();
      clearCustomerRingTimer();
      store.setCustomerRinging?.(false);
      store.setRingingActionsSuppressed?.(false);
      if (!store.activeCallMeta) {
        if (store.callState !== "idle") {
          store.setAnswering(false);
          store.endCall({ resetIdle: false });
        }
        lastEventKeyRef.current = null;
        return;
      }

      const answered = Boolean(store.activeCallMeta?.bridge_time);
      store.setAnswering(false);

      // A normal hangup/missed/declined call ending gives the agent no
      // surprise — they already saw it ring or talked on it. A call that
      // never got answered because the carrier itself rejected it (e.g.
      // "Unallocated (unassigned) number") looked identical to any other
      // silent disconnect, leaving the agent to guess why dialing just
      // "stopped" with no feedback at all.
      if (!answered) {
        const cause = String(evt.hangupCause || "").toLowerCase();
        const isFailure = cause.includes("unalloc")
          || cause.includes("unassigned")
          || cause.includes("invalid number")
          || cause.includes("no route")
          || cause.includes("not reachable")
          || cause.includes("congestion")
          || cause.includes("out of order")
          || cause.includes("failed");
        if (isFailure) {
          toast.error(`Call failed: ${evt.hangupCause}`);
        }
      }

      try {
        const audioEl = document.getElementById("hangupAudio");
        if (audioEl) {
          audioEl.pause();
          audioEl.currentTime = 0;
          audioEl.play().catch(() => {});
        }
      } catch {}

      try {
        store.completeActiveCall({
          answer_status: answered ? "answered" : "not_answered",
          end_time: evt.timestamp,
          hangup_cause: evt.hangupCause || undefined,
        });
      } catch (e) {
        console.error("[MobileCallSocket] completeActiveCall failed", e);
      }

      store.endCall({ resetIdle: answered });
      lastEventKeyRef.current = null;
    };

    const applyWebrtcCallData = (evt, payload) => {
      const store = useCallStore.getState();
      const clearCustomerRinging = () => {
        clearCustomerRingTimer();
        store.setCustomerRinging?.(false);
        store.setRingingActionsSuppressed?.(false);
      };

      if (evt.eventName === "HANGUP") {
        rememberWebrtcCustomer(null);
        clearCustomerRinging();
        return;
      }
      if (!["DIALING", "RINGING", "ANSWERED"].includes(evt.eventName)) return;

      const socketCustomer = lastTen(evt.customerNumber);
      if (socketCustomer) {
        rememberWebrtcCustomer({
          number: socketCustomer,
          traceId: evt.traceId || null,
          channelIds: [evt.channelId, evt.agentChannelId, evt.customerChannelId].filter(Boolean),
        });
        const metaTraceId = store.activeCallMeta?.trace_id;
        const sameCall = !(evt.traceId && metaTraceId && evt.traceId !== metaTraceId);
        if (
          sameCall &&
          store.direction === "incoming" &&
          store.callState !== "idle" &&
          !isOutboundCall(store) &&
          lastTen(store.callerNumber) !== socketCustomer
        ) {
          useCallStore.setState({ callerNumber: socketCustomer });
          store.updateActiveCallMeta?.({ caller_number: socketCustomer, customer_number: socketCustomer });
        }
      }

      const rawCallLeg = String(payload?.callLeg || payload?.call_leg || evt.callLeg || "").toUpperCase();
      store.setSipData({
        sipCampaignId: evt.campaignId || null,
        sipCallGroupId: evt.callGroupId || null,
        channelId: evt.channelId || null,
        agentChannelId: evt.agentChannelId || null,
        bPartyChannelId: rawCallLeg === "B_PARTY" ? evt.channelId || null : null,
        customerChannelId: evt.customerChannelId || null,
        sipLeadData: evt.leadData || null,
      });
      patchTraceId(store, evt);
      if (evt.callType || evt.isIvrSource) {
        store.setCallTypeInfo?.({ callType: evt.callType, isIvrSource: evt.isIvrSource });
      }

      if (evt.eventName === "ANSWERED") {
        clearCustomerRinging();
        if (rawCallLeg === "A_PARTY") store.updateActiveCallMeta?.({ customer_connected: true });
        return;
      }

      // WebRTC outbound: the server may not relay SIP 180/183 to the browser,
      // so the RINGING socket event also marks real ringing (status + live
      // timer start). The agent's own leg here is the browser call itself,
      // which never rings — so any RINGING before answer is the customer's,
      // whatever callLeg label it carries.
      if (
        evt.eventName === "RINGING" &&
        isOutboundCall(store) &&
        !store.activeCallMeta?.bridge_time
      ) {
        if (!store.activeCallMeta?.ring_time) {
          store.updateActiveCallMeta?.({ ring_time: evt.timestamp || new Date().toISOString() });
        }
        store.setOutgoingCallStatus("ringing-customer");
        return;
      }

      if (
        evt.eventName === "RINGING" &&
        evt.callLeg === "A_PARTY" &&
        store.activeCallMeta?.bridge_time &&
        !isOutboundCall(store)
      ) {
        store.setRingingActionsSuppressed?.(true);
        store.setCustomerRinging?.(true);
        clearCustomerRingTimer();
        customerRingTimerRef.current = setTimeout(() => {
          customerRingTimerRef.current = null;
          const s = useCallStore.getState();
          s.setCustomerRinging?.(false);
          s.setRingingActionsSuppressed?.(false);
        }, CUSTOMER_RINGING_MAX_MS);
      }
    };

    const ROUTES = {
      DIALING: handleDialing,
      RINGING: handleRinging,
      ANSWERED: handleAnswered,
      HANGUP: handleHangup,
    };

    const onAny = (channelName, ...args) => {
      const payload =
        args.length === 1 ? args[0] : args.length ? args : undefined;
      const evt = normalizeCallEvent(payload, channelName);

      if (eventBelongsToAgent(payload, { agentId, agentNumber })) {
        const eventPbxName = payload?.pbx_name || payload?.pbxName || null;
        if (eventPbxName) {
          useCallStore.getState().setSipData({ pbxName: String(eventPbxName) });
        }

        // IVR keys the customer pressed: `validDtmf` rides on the call events
        // (DIALING / RINGING) for both WebRTC and mobile profiles. Kept per trace id
        // so a later call's events never show an earlier call's keys.
        const rawDtmf = payload?.validDtmf ?? payload?.valid_dtmf;
        const dtmf = rawDtmf == null ? "" : String(rawDtmf).trim();
        if (dtmf && dtmf !== "null") {
          const keys = dtmf.split(",").map((d) => d.trim()).filter(Boolean);
          const traceId = evt.traceId || null;
          const prev = useCallStore.getState().ivrKeys;
          if (!prev || prev.traceId !== traceId || prev.keys.join(",") !== keys.join(",")) {
            useCallStore.setState({ ivrKeys: { traceId, keys } });
          }
        }
      }

      if (!isSocketCallDrivenProfileActive()) {
        if (isWebrtcProfileActive() && eventBelongsToAgent(payload, { agentId, agentNumber })) {
          applyWebrtcCallData(evt, payload);
        }
        return;
      }

      if (debugOn()) {
        console.log(
          `[MobileCallSocket] ⇐ "${channelName}"`,
          { raw: payload, normalized: evt }
        );
      }

      const handler = evt.eventName && ROUTES[evt.eventName];
      if (!handler) return;

      if (!eventBelongsToAgent(payload, { agentId, agentNumber })) {
        if (debugOn()) {
          console.log(
            `[MobileCallSocket] skip ${evt.eventName} — not this agent`,
            { eventAgentId: evt.agentId, myAgentId: agentId }
          );
        }
        return;
      }

      const rawCallLeg = String(
        payload?.callLeg || payload?.call_leg || evt.callLeg || ""
      ).toUpperCase();
      const bChannelId = payload?.channelId || evt.channelId || null;
      if (rawCallLeg === "B_PARTY" && bChannelId) {
        useCallStore.getState().setSipData({ bPartyChannelId: bChannelId });
      }

      if (isDuplicate(evt)) return;
      handler(evt);

      if (evt.callType || evt.isIvrSource) {
        useCallStore.getState().setCallTypeInfo?.({
          callType: evt.callType,
          isIvrSource: evt.isIvrSource,
        });
      }
    };

    socket.onAny(onAny);
    return () => {
      socket.offAny(onAny);
      clearCustomerRingTimer();
    };
  }, [agentId, agentNumber]);
}

export default useMobileCallSocket;
